
import { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface QualityMonthData {
  month: string;
  passRate: number;
  totalBatches: number;
  passedBatches: number;
  moistureAvg: number;
  impurityAvg: number;
  incompleteAvg: number;
}

const qualityTrendData: QualityMonthData[] = [
  { month: '9月', passRate: 91.2, totalBatches: 34, passedBatches: 31, moistureAvg: 12.8, impurityAvg: 0.9, incompleteAvg: 5.2 },
  { month: '10月', passRate: 93.5, totalBatches: 31, passedBatches: 29, moistureAvg: 12.5, impurityAvg: 0.85, incompleteAvg: 4.8 },
  { month: '11月', passRate: 89.7, totalBatches: 39, passedBatches: 35, moistureAvg: 13.1, impurityAvg: 1.05, incompleteAvg: 5.6 },
  { month: '12月', passRate: 94.1, totalBatches: 34, passedBatches: 32, moistureAvg: 12.3, impurityAvg: 0.78, incompleteAvg: 4.5 },
  { month: '1月', passRate: 96.3, totalBatches: 27, passedBatches: 26, moistureAvg: 12.0, impurityAvg: 0.72, incompleteAvg: 4.1 },
  { month: '2月', passRate: 95.0, totalBatches: 40, passedBatches: 38, moistureAvg: 12.2, impurityAvg: 0.76, incompleteAvg: 4.3 },
];

const varietyQuality = [
  { name: '小麦', passRate: 96.2, batches: 48, color: '#0c7362' },
  { name: '玉米', passRate: 93.8, batches: 35, color: '#f59e0b' },
  { name: '大米', passRate: 97.1, batches: 42, color: '#10b981' },
  { name: '大豆', passRate: 91.5, batches: 28, color: '#f97316' },
  { name: '高粱', passRate: 94.4, batches: 18, color: '#ef4444' },
];

export default function QualityTrendCard() {
  const [activeMetric, setActiveMetric] = useState<'passRate' | 'moisture' | 'impurity'>('passRate');

  const chartData = useMemo(() => {
    return qualityTrendData.map(d => ({
      name: d.month,
      value: activeMetric === 'passRate' ? d.passRate : activeMetric === 'moisture' ? d.moistureAvg : d.impurityAvg,
    }));
  }, [activeMetric]);

  const latestData = qualityTrendData[qualityTrendData.length - 1];
  const prevData = qualityTrendData[qualityTrendData.length - 2];
  const rateChange = (latestData.passRate - prevData.passRate).toFixed(1);
  const isUp = Number(rateChange) >= 0;

  const overallPassRate = useMemo(() => {
    const totalBatches = qualityTrendData.reduce((s, d) => s + d.totalBatches, 0);
    const totalPassed = qualityTrendData.reduce((s, d) => s + d.passedBatches, 0);
    return ((totalPassed / totalBatches) * 100).toFixed(1);
  }, []);

  const metricConfig = {
    passRate: { label: '合格率', unit: '%', color: '#0c7362', gradientId: 'qualityGrad1' },
    moisture: { label: '平均水分', unit: '%', color: '#3b82f6', gradientId: 'qualityGrad2' },
    impurity: { label: '平均杂质率', unit: '%', color: '#f59e0b', gradientId: 'qualityGrad3' },
  };

  const currentConfig = metricConfig[activeMetric];

  return (
    <div className="bg-white rounded-2xl p-6 shadow-soft">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-gray-800 flex items-center">
          <i className="ri-shield-check-line mr-2 text-[#0c7362]"></i>
          质量合格率趋势监控
        </h2>
        <div className="flex items-center space-x-1 bg-gray-100 rounded-xl p-1">
          {(['passRate', 'moisture', 'impurity'] as const).map(key => (
            <button
              key={key}
              onClick={() => setActiveMetric(key)}
              className={`px-3 py-1.5 text-xs rounded-lg font-medium whitespace-nowrap cursor-pointer transition-all ${
                activeMetric === key
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {metricConfig[key].label}
            </button>
          ))}
        </div>
      </div>

      {/* 顶部指标卡片 */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        <div className="bg-[#0c7362]/5 rounded-xl px-4 py-3">
          <div className="text-xs text-gray-500 mb-1">综合合格率</div>
          <div className="flex items-baseline">
            <span className="text-xl font-bold text-[#0c7362]">{overallPassRate}</span>
            <span className="text-xs text-gray-400 ml-1">%</span>
          </div>
        </div>
        <div className="bg-emerald-50 rounded-xl px-4 py-3">
          <div className="text-xs text-gray-500 mb-1">本月合格率</div>
          <div className="flex items-baseline">
            <span className="text-xl font-bold text-emerald-600">{latestData.passRate}</span>
            <span className="text-xs text-gray-400 ml-1">%</span>
            <span className={`text-xs ml-2 font-medium ${isUp ? 'text-emerald-500' : 'text-rose-500'}`}>
              {isUp ? '+' : ''}{rateChange}%
            </span>
          </div>
        </div>
        <div className="bg-amber-50 rounded-xl px-4 py-3">
          <div className="text-xs text-gray-500 mb-1">本月检测批次</div>
          <div className="flex items-baseline">
            <span className="text-xl font-bold text-amber-600">{latestData.totalBatches}</span>
            <span className="text-xs text-gray-400 ml-1">批</span>
          </div>
        </div>
        <div className="bg-rose-50 rounded-xl px-4 py-3">
          <div className="text-xs text-gray-500 mb-1">不合格批次</div>
          <div className="flex items-baseline">
            <span className="text-xl font-bold text-rose-500">{latestData.totalBatches - latestData.passedBatches}</span>
            <span className="text-xs text-gray-400 ml-1">批</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-5">
        {/* 趋势图 */}
        <div className="col-span-2">
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <defs>
                  <linearGradient id="qualityGrad1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0c7362" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#0c7362" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="qualityGrad2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="qualityGrad3" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  domain={activeMetric === 'passRate' ? [85, 100] : activeMetric === 'moisture' ? [10, 15] : [0, 2]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    fontSize: '13px',
                    padding: '10px 14px',
                  }}
                  formatter={(value: number) => [`${value}${currentConfig.unit}`, currentConfig.label]}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={currentConfig.color}
                  strokeWidth={2.5}
                  fill={`url(#${currentConfig.gradientId})`}
                  dot={{ r: 4, fill: currentConfig.color, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 6, fill: currentConfig.color, strokeWidth: 2, stroke: '#fff' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 分品种合格率 */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">分品种合格率</h3>
          <div className="space-y-3">
            {varietyQuality.map(v => (
              <div key={v.name}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">{v.name}</span>
                  <span className="text-xs font-semibold" style={{ color: v.color }}>{v.passRate}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${v.passRate}%`, backgroundColor: v.color }}
                  ></div>
                </div>
                <div className="text-[10px] text-gray-400 mt-0.5">{v.batches} 批次检测</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
